import { useState } from 'react';
import type { AdminUserListItem } from '@/types/api';
import { Dialog } from '@/components/ui/dialog';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { ConflictError, deactivateUser } from '../api/admin-users-api';

type Phase = 'idle' | 'submitting';

interface DeactivateUserConfirmProps {
  open: boolean;
  user: AdminUserListItem;
  currentAdminId: number;
  /** Called after the server returns 204 (and we're NOT force-logging-out). */
  onConfirmed: () => void;
  /** Called when the admin dismisses without confirming. */
  onClose: () => void;
}

export function DeactivateUserConfirm({
  open,
  user,
  currentAdminId,
  onConfirmed,
  onClose,
}: DeactivateUserConfirmProps) {
  const { logout } = useAuth();

  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // NOTE: this component is keyed on `user.id` by its parent, so each new
  // target gets a fresh component instance with reset phase/error state.

  const isSelf = user.id === currentAdminId;

  const handleClose = () => {
    if (phase === 'submitting') return;
    onClose();
  };

  const handleConfirm = async () => {
    setErrorMessage(null);
    setPhase('submitting');
    try {
      await deactivateUser(user.id);

      if (isSelf) {
        logout();
        return;
      }

      onConfirmed();
      onClose();
    } catch (err) {
      if (err instanceof ConflictError) {
        setErrorMessage(err.message);
        setPhase('idle');
        return;
      }
      setErrorMessage('Could not deactivate user. Check API connectivity and try again.');
      setPhase('idle');
    }
  };

  const displayName = `${user.firstName} ${user.lastName}`.trim() || user.email;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Deactivate user"
      dismissible={phase !== 'submitting'}
    >
      <div className="space-y-4" data-testid="deactivate-user-confirm">
        <p className="text-sm text-gray-300">
          Deactivate <span className="font-semibold text-white">{displayName}</span> (
          <span className="font-mono">{user.email}</span>)? They will lose access immediately. You
          can reactivate them via Edit.
        </p>

        {errorMessage && (
          <div
            role="alert"
            data-testid="deactivate-user-error"
            className="rounded border border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300"
          >
            {errorMessage}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid="deactivate-user-cancel-button"
            onClick={handleClose}
            disabled={phase === 'submitting'}
            className="rounded border border-gray-600 px-4 py-2 text-sm text-gray-200 hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="deactivate-user-confirm-button"
            onClick={handleConfirm}
            disabled={phase === 'submitting'}
            className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
          >
            {phase === 'submitting' ? 'Deactivating…' : 'Deactivate'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
